import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from "@mui/material";
import { IPedido } from "../types/index";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PetsIcon from "@mui/icons-material/Pets";
import { getLancamentosByDocumento } from "../services/index";

interface Props {
  open: boolean;
  onClose: () => void;
  pedido: IPedido | null;
}

interface LancamentoApi {
  Id: string;
  Codigo: number;
  NumeroDocumento: string;
  Valor: number;
  TotalRecebido: number;
  Quitado: boolean;
  Pagamentos?: Array<{
    Data: string;
    FormaPagamento: string;
    Valor: number;
    NumeroDocumento?: string;
    Conciliado: boolean;
  }>;
}

const ModalDetalhesPedido: React.FC<Props> = ({ open, onClose, pedido }) => {
  const [lancamentoAtual, setLancamentoAtual] = useState<LancamentoApi | null>(
    null
  );
  const [carregandoLancamento, setCarregandoLancamento] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Carregar dados do lançamento quando o modal abrir
  useEffect(() => {
    const carregarLancamento = async () => {
      if (pedido) {
        setCarregandoLancamento(true);
        setErro(null);

        try {
          // Buscar lançamento pelo número do documento (ex: "Pedido n°102")
          const numeroDocumento = `Pedido n°${pedido.Codigo}`;
          const lancamentos = await getLancamentosByDocumento(numeroDocumento);

          if (lancamentos && lancamentos.length > 0) {
            const lancamento = lancamentos[0];
            setLancamentoAtual(lancamento);
          } else {
            // Se não encontrar lançamento, limpar os dados
            setLancamentoAtual(null);
          }
        } catch (error) {
          setErro("Erro ao carregar dados do lançamento");
          setLancamentoAtual(null);
        } finally {
          setCarregandoLancamento(false);
        }
      }
    };

    if (open && pedido) {
      carregarLancamento();
    } else {
      // Reset quando fechar o modal
      setLancamentoAtual(null);
      setErro(null);
    }
  }, [pedido, open]);

  // Calcular total de pagamentos da API
  const totalPagamentos = useMemo(() => {
    return lancamentoAtual?.TotalRecebido || 0;
  }, [lancamentoAtual?.TotalRecebido]);

  // Calcular saldo restante
  const saldoRestante = useMemo(() => {
    const valorTotal = lancamentoAtual?.Valor || pedido?.ValorFinal || 0;
    return valorTotal - totalPagamentos;
  }, [lancamentoAtual?.Valor, pedido?.ValorFinal, totalPagamentos]);

  if (!pedido) return null;

  const formatCurrency = (value: number | undefined) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Data não informada";

    try {
      // Verificar se a data está no formato brasileiro "DD/MM/YYYY - HH:MM"
      if (dateString.includes(" - ")) {
        const [datePart, timePart] = dateString.split(" - ");
        const [day, month, year] = datePart.split("/");
        const [hour, minute] = timePart.split(":");

        // Criar data no formato correto para o JavaScript (YYYY-MM-DD)
        const isoString = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`;
        const date = new Date(isoString);

        if (isNaN(date.getTime())) {
          return dateString; // Retorna string original se não conseguir converter
        }

        return date.toLocaleDateString("pt-BR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          // hour: "2-digit",
          // minute: "2-digit",
        });
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("pt-BR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString; // Retorna string original em caso de erro
    }
  };

  const isVenda = pedido.Categoria === "Venda";
  const titulo = isVenda ? "Detalhes da Venda" : "Detalhes do Atendimento";
  const icone = isVenda ? <ShoppingCartIcon /> : <PetsIcon />;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "70vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: isVenda ? "#e3f2fd" : "#f3e5f5",
          color: isVenda ? "#1976d2" : "#7b1fa2",
        }}
      >
        {icone}
        {titulo}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Informações do Pedido */}
          <Paper sx={{ p: 2, backgroundColor: "#fafafa" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
              Informações do Pedido
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Código do Pedido
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  #{pedido.Codigo}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.Data}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Categoria
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.Categoria}
                </Typography>
              </Box>
              {/* <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color:
                      pedido.Status === "Concluído" ? "#4caf50" : "#ff9800",
                  }}
                >
                  {pedido.Status}
                </Typography>
              </Box> */}
            </Box>
          </Paper>

          {/* Itens do Pedido */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
              Itens do Pedido
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Qtd
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Valor Unit.
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pedido.Items.map((item: any, index: number) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.Nome || item.Descricao}
                        </Typography>
                        {item.Descricao && item.Nome !== item.Descricao && (
                          <Typography variant="caption" color="text.secondary">
                            {item.Descricao}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {item.Quantidade || 1}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.ValorUnitario)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {formatCurrency(
                          (item.Quantidade || 1) * (item.ValorTotal || 0)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: "#333" }}>
                Total: {formatCurrency(pedido.ValorFinal)}
              </Typography>
            </Box>
          </Paper>

          {/* Informações Financeiras - Valor Pago e Saldo */}
          {!isVenda && (
            <Paper sx={{ p: 2, backgroundColor: "#f0f8ff" }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
                Informações Financeiras
              </Typography>

              {erro && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {erro}
                </Alert>
              )}

              {carregandoLancamento ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Carregando dados financeiros...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Valor Total
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#1976d2" }}
                      >
                        {formatCurrency(
                          lancamentoAtual?.Valor || pedido.ValorFinal
                        )}
                      </Typography>
                      {lancamentoAtual && (
                        <Typography variant="caption" color="text.secondary">
                          Lançamento #{lancamentoAtual.Codigo}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Valor Pago
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#4caf50",
                        }}
                      >
                        {formatCurrency(totalPagamentos)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Saldo Restante
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: saldoRestante > 0 ? "#ff5722" : "#4caf50",
                        }}
                      >
                        {formatCurrency(saldoRestante)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Barra de progresso visual */}
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 8,
                        backgroundColor: "#e0e0e0",
                        borderRadius: 4,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          backgroundColor: "#4caf50",
                          width: `${Math.min((totalPagamentos / (lancamentoAtual?.Valor || pedido.ValorFinal || 1)) * 100, 100)}%`,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        0%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(
                          (totalPagamentos /
                            (lancamentoAtual?.Valor ||
                              pedido.ValorFinal ||
                              1)) *
                            100
                        )}
                        % pago
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        100%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status de Quitação */}
                  {lancamentoAtual && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1,
                        backgroundColor: lancamentoAtual.Quitado
                          ? "#e8f5e8"
                          : "#fff3e0",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: lancamentoAtual.Quitado
                            ? "#2e7d32"
                            : "#ed6c02",
                        }}
                      >
                        Status:{" "}
                        {lancamentoAtual.Quitado ? "Quitado" : "Pendente"}
                      </Typography>
                    </Box>
                  )}

                  {/* Lista de Pagamentos */}
                  {lancamentoAtual?.Pagamentos &&
                    lancamentoAtual.Pagamentos.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Pagamentos Registrados
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {lancamentoAtual.Pagamentos.map(
                            (pagamento, index) => (
                              <Box
                                key={index}
                                sx={{
                                  p: 1.5,
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: 1,
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {formatCurrency(pagamento.Valor)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {pagamento.FormaPagamento} •{" "}
                                      {formatDate(pagamento.Data)}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      backgroundColor: pagamento.Conciliado
                                        ? "#e8f5e8"
                                        : "#fff3e0",
                                      color: pagamento.Conciliado
                                        ? "#2e7d32"
                                        : "#ed6c02",
                                    }}
                                  >
                                    {pagamento.Conciliado
                                      ? "Conciliado"
                                      : "Pendente"}
                                  </Typography>
                                </Box>
                                {pagamento.NumeroDocumento && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    Documento: {pagamento.NumeroDocumento}
                                  </Typography>
                                )}
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Aviso quando não há lançamento */}
                  {!lancamentoAtual && !carregandoLancamento && !erro && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Nenhum lançamento financeiro encontrado para este pedido
                      na API externa.
                    </Alert>
                  )}
                </>
              )}
            </Paper>
          )}

          {/* Informações do Cliente */}
          <Paper sx={{ p: 2, backgroundColor: "#fafafa" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
              Informações do Cliente
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nome
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.Cliente}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.ClienteEmail || "Não informado"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  CNPJ/CPF
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.ClienteCNPJ || "Não informado"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Vendedor
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {pedido.Vendedor || "Não informado"}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Informações Específicas de Ordem de Serviço */}
          {pedido.OrdemServico && pedido.Categoria === "Atendimento" && (
            <Paper sx={{ p: 2, backgroundColor: "#fff3e0" }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
                🏥 Detalhes da Ordem de Serviço
              </Typography>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Técnico
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pedido.OrdemServico.Tecnico || "Não informado"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data de Abertura
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pedido.OrdemServico.DataAbertura
                      ? formatDate(pedido.OrdemServico.DataAbertura)
                      : "Não informado"}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="body2" color="text.secondary">
                    Problema Relatado
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>
                    {pedido.OrdemServico.Problema || "Não informado"}
                  </Typography>
                </Box>
                {pedido.OrdemServico.Laudo && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="body2" color="text.secondary">
                      Laudo
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>
                      {pedido.OrdemServico.Laudo}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Informações de Pagamento */}
          {pedido.Pagamentos && pedido.Pagamentos.length > 0 && (
            <Paper sx={{ p: 2, backgroundColor: "#e8f5e8" }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
                Informações de Pagamento
              </Typography>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Forma de Pagamento
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pedido.FormaPagamento || "Não informado"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Número de Parcelas
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pedido.NumeroParcelas
                      ? `${pedido.NumeroParcelas}x`
                      : "À vista"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: pedido.Finalizado ? "#4caf50" : "#ff9800",
                    }}
                  >
                    {pedido.Finalizado ? "Finalizado" : "Em andamento"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#fafafa" }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 100 }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDetalhesPedido;
